import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../api';
import TopBar from '../../Tools/Topbar';
import LeftSide from '../../Tools/LeftSidebar';
import Settings from '../../Tools/Setting/Settings';
import useSettings from '../../Tools/Setting/useSettings';
import Pagination from '../../Tools/Pagination';
import CaseEditmodal from './EditModel/CaseEditmodal'
import { format } from 'date-fns';


// EMSRequesterTab component manages the EMS case requests interface
const EMSRequesterTab = () => {
  // State for managing sidebar visibility
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  // State for storing EMS cases data
  const [emsCases, setEmsCases] = useState([]);
  // State for storing filtered EMS cases
  const [filteredEmsCases, setFilteredEmsCases] = useState([]);
  // State for loading status
  const [isLoading, setIsLoading] = useState(true);
  // State for error messages
  const [error, setError] = useState(null);
  // State for current page number
  const [page, setPage] = useState(1);
  // State for total number of cases
  const [total, setTotal] = useState(0);
  // State for items per page
  const [limit] = useState(10);
  // State for user role ID
  const [roleId, setRoleId] = useState(sessionStorage.getItem('Role_id'));
  // State for filter form inputs
  const [filterForm, setFilterForm] = useState({ name: '' });
  // State for controlling modal visibility
  const [showModal, setShowModal] = useState(false);
  // State for edit mode status
  const [isEditMode, setIsEditMode] = useState(false);
  // Get user ID from session storage
  const UserID = sessionStorage.getItem('user_id');
  // State for current case ID
  const [currentCaseId, setCurrentCaseId] = useState(null);
  
  // Get date and time format from sessionStorage
  const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
  const timeFormat = sessionStorage.getItem('time_format') || '24';


  // Navigation hook for routing
  const navigate = useNavigate();
  // Custom hook for settings management
  const { settings, updateSetting, handleColorTheme } = useSettings();
  // Reference for settings component
  const settingsRef = useRef(null);

  // Toggle sidebar visibility
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const formatCaseDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
    const timeFormat = sessionStorage.getItem('time_format') || '24';

    // Map WordPress-style formats to date-fns syntax
    const dateFormatMap = {
      'DD/MM/YYYY': 'dd/MM/yyyy',
      'MM/DD/YYYY': 'MM/dd/yyyy',
      'YYYY-MM-DD': 'yyyy-MM-dd',
    };

    const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
    const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;

    // ✅ Parse both ISO and non-ISO timestamps as *UTC*, not local time
    let utcDate;
    if (dateString.includes('T')) {
      // ISO 8601 with timezone (e.g. 2025-10-27T16:27:19.000000Z)
      utcDate = new Date(dateString);
    } else {
      // MySQL-like (e.g. 2025-10-27 16:35:40)
      utcDate = new Date(dateString.replace(' ', 'T') + 'Z'); // force UTC
    }

    // Format without applying local timezone
    const utcTimestamp = new Date(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
      utcDate.getUTCHours(),
      utcDate.getUTCMinutes(),
      utcDate.getUTCSeconds()
    );

    return format(utcTimestamp, formatString);
  } catch (err) {
    console.error('Error formatting date:', err, dateString);
    return 'Invalid Date';
  }
};

  // State for form data used in add/edit modal
  const [formData, setFormData] = useState({
    user_id: UserID,
    case_name: '',
    case_location: '',
    case_area: '',
    nearest_medical_center: '',
    injury_type: '',
    age: '',
    gender: '',
    triage_card: '',
    bracelet_id: '',
    contact_number: '',
    documents: [],
    ems_member: '',
    ems_member_name: '',
    nearest_ambulance: '',
    location: null,
    area: null,
    medical_center: null
  });

  // Open modal for adding a new case
  const openAddModal = () => {
    setIsEditMode(false);
    setCurrentCaseId(null);
    setFormData({
      case_name: '',
      case_location: '',
      case_area: '',
      nearest_medical_center: '',
      injury_type: '',
      age: '',
      gender: '',
      triage_card: '',
      bracelet_id: '',
      contact_number: '',
      documents: [],
      location: null,
      area: null,
      medical_center: null
    });
    setShowModal(true);
  };

  // Handle viewing case details
  const handleViewDetails = (caseItem) => {
    sessionStorage.setItem("currentCaseId", caseItem.id);
    sessionStorage.setItem("RequestTabCaseStatus", caseItem.case_status);
    navigate('/ems/requests-detail');
  };

  // Handle filter input changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterForm((prev) => ({ ...prev, [name]: value }));
  };

  // Clear filter inputs
  const clearFilters = () => setFilterForm({ name: '' });

  // Display alert messages
  const showAlert = (message, isSuccess = true) => 
    alert(`${isSuccess ? 'Success:' : 'Error:'} ${message}`);

  // Fetch EMS case requests from API
  const fetchCaseRequests = async () => {
    try {
      setIsLoading(true);
      if (!roleId) {
        setError('No role ID found. Please log in again.');
        setIsLoading(false);
        navigate('/login');
        return;
      }
      if (!sessionStorage.getItem('auth_token')) {
        setError('No authentication token found. Please log in.');
        setIsLoading(false);
        navigate('/login');
        return;
      }
      const userId = sessionStorage.getItem('user_id');
      const response = await axiosInstance.get(`/ems/records/${roleId}/ems_case_request/${userId}`);
      const data = response.data.data || response.data;
      setEmsCases(data.map(item => ({
        id: item.id,
        caseLocation: item.location?.location_name || 'N/A',
        nearestAmbulance: item.ambulance?.plate_number || 'N/A',
        MedicalcenterFrom: item.from_medical_center?.name || 'N/A',
        MedicalcenterTo: item.medical_center?.name || 'N/A',
        caseName: item.case_name || 'N/A',
        gender: item.gender || 'N/A',
        age: item.age || 'N/A',
        injuryType: item.injury_type || 'N/A',
        contactNumber: item.contact_number || 'N/A',
        emsMemberName: item.ems_member_name || 'N/A',
        created_at: item.created_at || null,
        dateTime: item.created_at ? new Date(item.created_at).toLocaleString() : 'N/A',
        patientSituation: item.patient_situation || 'Patient Situation',
        case_status: item.case_status
      })));

      setTotal(response.data.total || data.length);
      setError(null);
    } catch (err) {
      console.error('Error fetching EMS case requests:', err);
      if (err.response?.status === 404) {
        setError('No EMS case requests found for this role.');
      } else if (err.response?.status === 401) {
        setError('Unauthorized. Please log in again.');
        navigate('/login');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch EMS case requests. Please check the API endpoint or server status.');
      }
    } finally {
      setIsLoading(false);
    }
  };


  // Handle saving case data (add or edit)
  const handleSave = async (formDataToSend) => {
    try {
      setIsLoading(true);
      const token = sessionStorage.getItem('auth_token');
      
      const url = isEditMode ? `/ems/cases/${currentCaseId}` : '/ems/cases';
      await axiosInstance.post(url, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
      
      showAlert(`Case ${isEditMode ? 'updated' : 'added'} successfully!`);
      setShowModal(false);
      fetchCaseRequests();
    } catch (error) {
      console.error('Save failed:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save case';
      showAlert(errorMessage, false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle editing an existing case
  const handleEdit = async (caseItem) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/ems/cases/${caseItem.id}`);
      const caseData = response.data.data || response.data;
      
      setIsEditMode(true);
      setCurrentCaseId(caseItem.id);
      setFormData({
        case_name: caseData.case_name || '',
        case_location: caseData.case_location || '',
        case_area: caseData.case_area || '',
        nearest_medical_center: caseData.nearest_medical_center || '',
        injury_type: caseData.injury_type || '',
        age: caseData.age || '',
        gender: caseData.gender || '',
        triage_card: caseData.triage_card || '',
        bracelet_id: caseData.bracelet_id || '',
        contact_number: caseData.contact_number || '',
        documents: caseData.documents || [],
        case_status: caseData.case_status || 1,
        medical_center: caseData.medical_center || null,
        from_medical_center: caseData.from_medical_center || null,
        location: caseData.location || null,
        area: caseData.area || null
      });
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching case details:', error);
      showAlert('Failed to load case details', false);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter EMS cases based on filter form input
  useEffect(() => {
    setFilteredEmsCases(
      emsCases.filter((caseItem) =>
        filterForm.name
          ? caseItem.caseName.toLowerCase().includes(filterForm.name.toLowerCase())
          : true
      )
    );
  }, [emsCases, filterForm]);

  // Fetch case requests on component mount or when roleId/page changes
  useEffect(() => {
    fetchCaseRequests();
  }, [roleId, page, navigate]);

  // Map case status codes to display names
  const statusMap = {
    1: "New Case",
    2: "Transfer Case"
  };

  // Get current page items for pagination
  const currentItems = filteredEmsCases.slice((page - 1) * limit, page * limit);

  // Render the component
  return (
    <div id="main-wrapper">
      <LeftSide isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <TopBar toggleSidebar={toggleSidebar} />
        <div className="body-wrapper">
          <div className="container-fluid">
            <Settings
              ref={settingsRef}
              updateSetting={updateSetting}
              handleColorTheme={handleColorTheme}
              settings={settings}
            />
            <div className="card w-100 position-relative overflow-hidden">
              <div className="px-4 py-3">
                <h5 className="card-title fw-semibold mb-0 lh-sm">EMS Requests</h5>
                <p className="card-subtitle mb-0 mt-1">View and track all EMS Requests</p>
              </div>
              <div className="card-body p-4">
                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="filter-search mt-5">
                      <h6 className="px-2 mb-0">Filter</h6>
                      <div className="filter-panel">
                        <form className="row">
                          <div className="form-group col-md-6 col-lg-6 col-12 p-2">
                            <input
                              type="text"
                              className="form-control"
                              name="name"
                              placeholder="Name"
                              value={filterForm.name}
                              onChange={handleFilterChange}
                            />
                          </div>
                          <div className="form-group col-lg-2 p-2">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-rounded"
                              onClick={clearFilters}
                            >
                              Clear Filter
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                    <div className="py-3 px-0">
                      <div className="table-responsive rounded-2 ros-table">
                        <table className="table text-nowrap customize-table mb-0 align-middle">
                          <thead className="text-dark fs-4">
                            <tr>
                              <th><h6 className="fw-semibold mb-0">ID</h6></th>
                              <th><h6 className="fw-semibold mb-0">Case name</h6></th>
                              <th><h6 className="fw-semibold mb-0">Ambulance</h6></th>
                              <th><h6 className="fw-semibold mb-0">Transfer From</h6></th>
                              <th><h6 className="fw-semibold mb-0">Transfer To</h6></th>
                              <th><h6 className="fw-semibold mb-0">Status</h6></th>
                              <th><h6 className="fw-semibold mb-0">Case Date</h6></th>
                              <th><h6 className="fw-semibold mb-0">Action</h6></th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentItems.length === 0 ? (
                              <tr>
                                <td colSpan="12" className="text-center py-4">No EMS Cases Found</td>
                              </tr>
                            ) : (
                              currentItems.map((caseItem) => (
                                <tr key={caseItem.id}>
                                  <td>
                                    <button
                                      className="text-primary border-0 bg-transparent p-0"
                                      onClick={() => handleViewDetails(caseItem)}
                                    >
                                      {caseItem.id}
                                    </button>
                                  </td>
                                  <td>
                                    <button
                                      className="text-primary border-0 bg-transparent p-0"
                                      onClick={() => handleViewDetails(caseItem)}
                                    >
                                      {caseItem.caseName}
                                    </button>
                                  </td>
                                  <td>{caseItem.nearestAmbulance}</td>
                                  <td>{caseItem.MedicalcenterFrom}</td>
                                  <td>{caseItem.MedicalcenterTo}</td>
                                  <td>{statusMap[Number(caseItem.case_status)] || "N/A"}</td>
                                  <td>
                                    {caseItem.created_at ? formatCaseDate(caseItem.created_at) : 'N/A'}
                                  </td>
                                  <td>
                                    <button
                                      className="h4 me-2 border-0 bg-transparent"
                                      onClick={() => handleEdit(caseItem)}
                                      aria-label="Edit case"
                                      disabled={isLoading}
                                    >
                                      <i className="ti ti-edit text-warning"></i>
                                    </button>
                                    <button
                                      className="text-primary border-0 bg-transparent p-0"
                                      onClick={() => handleViewDetails(caseItem)}
                                    >
                                      <i className="ti ti-list-details text-success"></i>
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {filteredEmsCases.length > 0 && (
                      <Pagination total={total} limit={limit} page={page} onPageChange={setPage} />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <CaseEditmodal
        show={showModal}
        onClose={() => setShowModal(false)}
        formData={formData}
        onChange={(e) => {
          const { name, value } = e.target;
          setFormData((prev) => ({ ...prev, [name]: value }));
        }}
        onSave={handleSave}
        isEditMode={isEditMode}
        isLoading={isLoading}
      />
    </div>
  );
};

// Export the EMSRequesterTab component
export default EMSRequesterTab;